import useEventListener from '@hooks/use-event-listener';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Track changes to an Elements focus state over time
 * @param targetNode Element whose focus state we want to track
 * @param resetOnChange If the state should reset if the element passed in targetNode changes
 */
export default function useOnFocus(
  targetNode: HTMLElement | SVGElement | undefined,
  resetOnChange: boolean = false,
): [boolean, boolean, boolean, () => void] {
  const [isFocused, setIsFocused] = useState(false);
  const [hasFocused, setHasFocused] = useState(false);
  const [hasBlurred, setHasBlurred] = useState(false);

  const currentNode = useRef<HTMLElement | SVGElement>(undefined);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    setHasFocused(true);
  }, []);
  const handleBlur = useCallback(() => {
    setIsFocused(false);
    setHasBlurred(true);
  }, []);

  const reset = useCallback(() => {
    setIsFocused(false);
    setHasFocused(false);
    setHasBlurred(false);
  }, []);

  // reset when the targetNode changes
  useEffect(() => {
    if (resetOnChange && targetNode !== currentNode.current) {
      currentNode.current = targetNode;
      reset();
    }
  }, [targetNode, resetOnChange, reset]);

  useEventListener('focus', handleFocus, targetNode);
  useEventListener('blur', handleBlur, targetNode);

  return [isFocused, hasFocused, hasBlurred, reset];
}
